import { useEffect, useRef, useState } from "react";
import api, { apiError, openFileDownload } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, Video, X, Loader2, ExternalLink } from "lucide-react";

/**
 * Receipt / document uploader.
 * props:
 *   value: string id (single) | array of ids (multiple)
 *   onChange(newValue)
 *   multiple: bool
 *   label: string
 */
export default function FileUpload({ value, onChange, multiple = false, label = "Receipt / Document", testid = "file-upload", capture }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [meta, setMeta] = useState({}); // id -> {original_filename,is_image}

  const ids = multiple ? (value || []) : (value ? [value] : []);

  useEffect(() => {
    ids.forEach(async (id) => {
      if (id && !meta[id]) {
        try { const { data } = await api.get(`/files/${id}/meta`); setMeta((m) => ({ ...m, [id]: data })); }
        catch (e) { /* ignore */ }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data);
        setMeta((m) => ({ ...m, [data.id]: data }));
      }
      if (multiple) onChange([...(value || []), ...uploaded.map((u) => u.id)]);
      else onChange(uploaded[0].id);
      toast.success(uploaded.length > 1 ? `${uploaded.length} files uploaded` : "File uploaded");
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = (id) => {
    if (multiple) onChange((value || []).filter((x) => x !== id));
    else onChange("");
  };

  const view = async (id) => {
    try { await openFileDownload(id); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid={testid}>
      {label && <div className="text-xs text-slate-500 mb-1">{label}</div>}
      <div className="flex flex-wrap gap-2 mb-2">
        {ids.map((id) => {
          const m = meta[id] || {};
          return (
            <div key={id} className="flex items-center gap-2 border border-slate-200 rounded-sm px-2 py-1 bg-slate-50 text-xs" data-testid={`file-chip-${id}`}>
              {m.is_image ? <ImageIcon className="h-3.5 w-3.5 text-blue-600" /> : m.is_video ? <Video className="h-3.5 w-3.5 text-violet-600" /> : <FileText className="h-3.5 w-3.5 text-slate-500" />}
              <button type="button" onClick={() => view(id)} className="text-blue-600 hover:underline max-w-[140px] truncate flex items-center gap-1" data-testid={`file-view-${id}`}>
                {m.original_filename || "file"} <ExternalLink className="h-3 w-3" />
              </button>
              <button type="button" onClick={() => remove(id)} className="text-slate-400 hover:text-red-600" data-testid={`file-remove-${id}`}><X className="h-3.5 w-3.5" /></button>
            </div>
          );
        })}
      </div>
      <input ref={fileRef} type="file" accept={capture ? "image/*" : ".jpg,.jpeg,.png,.webp,.gif,.heic,.heif,.pdf,.mp4,.mov,.webm"} capture={capture} multiple={multiple}
        className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid={`${testid}-input`} />
      <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid={`${testid}-btn`}>
        {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
        {uploading ? "Uploading…" : (ids.length && !multiple ? "Replace file" : "Upload")}
      </Button>
      <span className="text-[11px] text-slate-400 ml-2">Photos, PDF, MP4, MOV, WEBM · max 50MB each</span>
    </div>
  );
}

export function FileLink({ id }) {
  const [m, setM] = useState(null);
  useEffect(() => { if (id) api.get(`/files/${id}/meta`).then(({ data }) => setM(data)).catch(() => {}); }, [id]);
  if (!id) return <span className="text-slate-300">—</span>;
  const view = async () => {
    try { await openFileDownload(id); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <button type="button" onClick={view} className="text-blue-600 hover:underline text-xs inline-flex items-center gap-1" data-testid={`filelink-${id}`}>
      {m?.is_image ? <ImageIcon className="h-3.5 w-3.5" /> : m?.is_video ? <Video className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
      {m?.original_filename || "View"}
    </button>
  );
}
